import React, { useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';
import {
  Favorite as FavoriteIcon,
  LibraryBooks as LibraryBooksIcon
} from '@material-ui/icons';

import { SolutionGalleryModal } from '../components/Modal';
import Solution from '../models/Solution';
import useSnackbar from './useSnackbar';
import { useDialog } from '../context/Dialog';

interface renderOpenButtonProps {
  specId: string
}

interface renderSaveButtonProps {
  specId: string
  code: string
}

const useSolutionGallery = () => {
  // modal
  const [isGalleryOpen, setIsGalleryOpen] = useState<boolean>(false);
  const [gallerySpecId, setGallerySpecId] = useState<string>('');

  const { confirm } = useDialog();

  const openGallery = (specId: string) => {
    setGallerySpecId(specId);
    setIsGalleryOpen(true);
  };

  // save
  const [isSavingCode, setIsSavingCode] = useState<boolean>(false);
  const [saveStatusMap] = useState<Map<string,boolean>>(new Map());
  const saveCode = async (specId: string, code: string) => {
    try {
      saveStatusMap.set(specId, true);
      setIsSavingCode(true);
      if (await confirm('Do you want to save the code to solution gallery?')) {
        await (new Solution({
          specID: specId,
          name: `${specId} [${new Date().toLocaleString()}]`,
          code: code
        })).create();

        showSnackbarMessage( 'Code has been successfully saved.', 'success');
        setGallerySpecId('');
      }
    }
    catch (e) {
      showSnackbarMessage(`Unable to save code: ${e.message}`, 'error');
    }
    finally {
      saveStatusMap.set(specId, false);
      setIsSavingCode(false);
    }
  };

  // snackbar
  const { renderSnackbar, showMessage: showSnackbarMessage } = useSnackbar();

  return {
    renderModal() {
      return (
        <SolutionGalleryModal
          isOpen={isGalleryOpen}
          specId={gallerySpecId}
          onClose={() => setIsGalleryOpen(false)}
        />
      );
    },

    renderOpenButton(props: renderOpenButtonProps) {
      return (
        <Button
          type="button"
          size="small"
          onClick={() => openGallery(props.specId)}
          variant="outlined"
          color="primary"
          startIcon={<LibraryBooksIcon />}
        >
          Solutions
        </Button>
      );
    },

    renderSaveButton(props: renderSaveButtonProps) {
      return (
        <Button
          type="button"
          size="small"
          onClick={() => saveCode(props.specId, props.code)}
          variant="outlined"
          color="primary"
          startIcon={isSavingCode && !!saveStatusMap.get(props.specId)? (<CircularProgress size="1em" color="inherit" />) : (<FavoriteIcon />)}
          disabled={(isSavingCode && !!saveStatusMap.get(props.specId)) || !props.code.trim()}
        >
          Save
        </Button>
      );
    },

    renderSnackbar
  };
};

export default useSolutionGallery;
